import { Fragment, useEffect, useRef, useState } from 'react'

import { Combobox } from '@headlessui/react'
import { clsx } from 'clsx'
import isEqual from 'react-fast-compare'
import { useNavigate } from 'react-router-dom'

import { usePrevious } from '@bettermode/common/hooks'
import { isInternalLink } from '@bettermode/common/url'
import { useTribeClient } from '@tribeplatform/react-sdk'
import { useNetwork } from '@tribeplatform/react-sdk/hooks'

import { ActionItem, useCommandBar } from '../CommandBar/index.js'

import { AskAIDisclaimer } from './components/AskAI/index.js'
import {
  TrendingPosts,
  AskAIResultRow,
  SmartSearchHeader,
  LegacySearchResults,
  AiPoweredSearchResults,
  SmartSearchHeaderRef,
  useRecentSearch,
  RecentSearches,
  SearchHistory,
  AskAI,
  useSearchHistory,
} from './components/index.js'
import { useSmartSearchUrlParams } from './hooks/index.js'
import {
  BLUR_TIMEOUT,
  SMART_SEARCH_FILTERS_INITIAL_STATE,
  SMART_SEARCH_HEADER_INPUT_DEFAULT_HEIGHT,
} from './SmartSearch.constants.js'
import {
  ResultsView,
  SmartSearchFilters,
  SmartSearchTabs,
  SmartSearchTabsType,
} from './SmartSearch.types.js'

interface Props {
  className?: string
  view: ResultsView
}

export const SmartSearch = ({ className, view }: Props) => {
  const { data } = useNetwork({
    fields: {
      searchSettings: 'all',
    },
  })
  const searchSettings = data?.searchSettings
  const { submit: submitRecentSearchQuery } = useRecentSearch()
  const navigate = useNavigate()
  const { close, searchOptions } = useCommandBar()
  const {
    config: {
      enableVectorSearch: isVectorSearchEnabled,
      enableAiSearch: isAiSearchEnabled,
    },
  } = useTribeClient()

  const {
    query: maybeQuery,
    setParams,
    view: maybeView,
    type: maybeType,
    spaceIds: maybeSpaceIds,
    fetchMore,
    isInSearchPage,
  } = useSmartSearchUrlParams()

  const isContextSpecificSearch = !!searchOptions?.entityType
  const maybePlaceholder = isContextSpecificSearch && searchOptions?.placeholder

  const headerRef = useRef<SmartSearchHeaderRef>(null)

  const [filters, setFilters] = useState<SmartSearchFilters>({
    ...SMART_SEARCH_FILTERS_INITIAL_STATE,
    query: maybeQuery || '',
    type: searchOptions.entityType ?? maybeType,
    askAIQuery: maybeQuery || '',
    spaceIds: searchOptions.spaceIds ?? maybeSpaceIds,
  })

  const [activeTab, setActiveTab] = useState<SmartSearchTabsType>(
    maybeView || SmartSearchTabs.SEARCH,
  )

  useEffect(() => {
    if (!isInSearchPage) {
      return
    }

    setActiveTab(maybeView || SmartSearchTabs.SEARCH)
  }, [maybeView, isInSearchPage])

  useEffect(() => {
    if (!isInSearchPage) {
      return
    }

    setFilters(prev => ({ ...prev, type: maybeType }))
  }, [maybeType, isInSearchPage])

  useEffect(() => {
    if (!isInSearchPage || isEqual(maybeSpaceIds, filters.spaceIds)) {
      return
    }

    setFilters(prev => ({ ...prev, spaceIds: maybeSpaceIds }))
    // we only want to run this effect when the value in the URL changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maybeSpaceIds, isInSearchPage])

  const showSearchInitialView =
    !filters.query && activeTab === SmartSearchTabs.SEARCH

  const handleAskAIResultSubmit = (newQuery?: string) => {
    if (!isAiSearchEnabled) {
      return
    }

    setActiveTab(SmartSearchTabs.ASK_AI)
    setFilters(prev => ({
      ...prev,
      askAIQuery: newQuery || prev.query,
      ...(newQuery ? { query: newQuery } : {}),
    }))

    setTimeout(() => {
      // to keep the user's attention on the AI response, we blur the input if the height is more than 1 row
      if (
        headerRef.current?.getHeight() >
        SMART_SEARCH_HEADER_INPUT_DEFAULT_HEIGHT
      ) {
        headerRef.current?.blur()
      }
    }, BLUR_TIMEOUT)
  }

  const resetToInitialState = () => {
    setFilters(SMART_SEARCH_FILTERS_INITIAL_STATE)
    setActiveTab(SmartSearchTabs.SEARCH)
  }

  const { submitItem: submitToSearchHistory } = useSearchHistory()

  const onClose = () => {
    if (view === 'search-page') {
      return
    }

    close()

    resetToInitialState()
  }

  const runAction = (actionItem: ActionItem) => {
    if (!actionItem.preventClose) {
      onClose()
    }

    if (typeof actionItem?.action === 'function') {
      actionItem.action()
      return
    }

    if (actionItem?.item && actionItem?.entityType) {
      submitToSearchHistory(actionItem.item, actionItem.entityType)
    }

    submitRecentSearchQuery(filters.query)

    // Combobox doesn't handle navigation when item is selected by pressing Enter
    // it needs to be handled manually in the onChange event.
    // https://github.com/tailwindlabs/headlessui/discussions/1821
    if (actionItem?.url) {
      const isInternal = isInternalLink(actionItem.url)
      if (isInternal) {
        navigate(actionItem.url)
      } else {
        window.open(actionItem.url, '_blank')
      }
    }
  }

  const handleInteractionWithResultsArea = () => {
    submitRecentSearchQuery(filters.query)
  }

  const isAskAIResultRowHidden =
    activeTab === SmartSearchTabs.ASK_AI &&
    (filters.query.trim() === '' || filters.query === filters.askAIQuery)

  const prevQuery = usePrevious(filters.query)

  // keep query params in sync with the state in search page
  useEffect(() => {
    // checking prevQuery to avoid updates on initial landing on search page
    if (!isInSearchPage || !prevQuery) {
      return
    }

    setParams({
      newQuery: filters.query,
      newType: fetchMore ? maybeType : null,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.query])

  const showTrendingPosts =
    isVectorSearchEnabled &&
    (!isContextSpecificSearch ||
      (isContextSpecificSearch && searchOptions.spaceIds?.length > 0))

  return (
    <Combobox
      onChange={item => runAction(item)}
      value={null}
      as="div"
      className={clsx(
        'combobox-wrapper flex flex-col',
        {
          'divide-y divide-line-subdued': !isAskAIResultRowHidden,
        },
        className,
      )}
    >
      <SmartSearchHeader
        view={view}
        filters={filters}
        setFilters={setFilters}
        activeTab={activeTab}
        onBack={resetToInitialState}
        ref={headerRef}
        aiEnabled={isAiSearchEnabled}
        placeholder={maybePlaceholder}
      />

      <div
        className={clsx(
          'flex flex-col flex-1 overflow-hidden divide-y divide-line-subdued',
          {
            'overflow-y-auto overflow-x-hidden': showSearchInitialView,
          },
        )}
        onClick={handleInteractionWithResultsArea}
      >
        {isAiSearchEnabled && !isContextSpecificSearch && (
          <AskAIResultRow
            text={filters.query}
            isHidden={isAskAIResultRowHidden}
            onSubmit={handleAskAIResultSubmit}
            view={view}
          />
        )}

        {activeTab === SmartSearchTabs.SEARCH &&
          (showSearchInitialView ? (
            <div className="flex flex-1 flex-col divide-y divide-line-subdued">
              {!isContextSpecificSearch && (
                <>
                  <RecentSearches
                    onItemClick={query =>
                      setFilters(prev => ({ ...prev, query }))
                    }
                    view={view}
                  />

                  <SearchHistory view={view} />
                </>
              )}

              {showTrendingPosts && <TrendingPosts view={view} />}
            </div>
          ) : (
            <section className="flex flex-col flex-1 h-full overflow-y-auto overflow-x-hidden divide-y divide-line-subdued">
              {isVectorSearchEnabled ? (
                <AiPoweredSearchResults
                  filters={filters}
                  setFilters={setFilters}
                  view={view}
                  searchNetworkSettings={searchSettings}
                />
              ) : (
                <LegacySearchResults
                  filters={filters}
                  setFilters={setFilters}
                  view={view}
                />
              )}
            </section>
          ))}

        {activeTab === SmartSearchTabs.ASK_AI && (
          <section className="flex flex-col h-full">
            <AskAIDisclaimer />

            <div className="flex flex-col h-full overflow-y-auto overflow-x-hidden divide-y divide-line-subdued">
              <AskAI
                question={filters.askAIQuery}
                onSuggestionClick={handleAskAIResultSubmit}
                view={view}
                hasNewQuestionWaiting={
                  filters.query.trim().length &&
                  filters.query !== filters.askAIQuery
                }
                onSwitchToResults={type => {
                  setActiveTab(SmartSearchTabs.SEARCH)
                  setFilters(prev => ({ ...prev, type }))
                }}
              />
            </div>
          </section>
        )}
      </div>
    </Combobox>
  )
}
